import {
    isValidElement,
    Children,
    useMemo,
    type FC
} from "react";
import {
    type ViewStyle,
    StyleSheet,
    View
} from "react-native";
import type IMessageScrollerProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import MessageScrollerButton from "./components/button";
import MessageScrollerContent from "./components/content";
import MessageScrollerFade from "./components/fade";
import MessageScrollerFooter from "./components/footer";
import MessageScrollerItem from "./components/item";
import MessageScrollerLoader from "./components/loader";
import MessageScrollerProvider from "./components/provider";
import MessageScrollerViewport from "./components/viewport";
import {
    MessageScrollerRootContext,
    useMessageScrollerContext,
    useMessageScrollerState
} from "./context";
import {
    NCoreUIKitTheme
} from "../../core/hooks";

const MessageScrollerRoot: FC<IMessageScrollerProps> = ({
    customLocalize,
    customTheme,
    children,
    style,
    title,
    ...props
}) => {
    const {
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const backgroundColor = StyleSheet.flatten<ViewStyle>(style)?.backgroundColor;

    const fadeColor = typeof backgroundColor === "string" ? backgroundColor : colors.content.container.default;

    const {
        container: containerDynamicStyle
    } = useStyles({
        fadeColor
    });

    const rootContextValue = useMemo(() => ({
        customLocalize,
        customTheme,
        fadeColor,
        title
    }), [
        customLocalize,
        customTheme,
        fadeColor,
        title
    ]);

    const items = Children.toArray(children);

    const footers = items.filter((child) => isValidElement(child) && child.type === MessageScrollerFooter);

    const body = items.filter((child) => !footers.includes(child));

    return <MessageScrollerRootContext.Provider
        value={rootContextValue}
    >
        <View
            {...props}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
        >
            <View
                style={stylesheet.main}
            >
                {body}
                <MessageScrollerFade
                    customTheme={customTheme}
                    edge="start"
                />
                <MessageScrollerFade
                    customTheme={customTheme}
                    edge="end"
                />
                <MessageScrollerLoader
                    customTheme={customTheme}
                />
            </View>
            {footers}
        </View>
    </MessageScrollerRootContext.Provider>;
};

const MessageScroller: FC<IMessageScrollerProps> = (props) => {
    const context = useMessageScrollerContext();

    if(context) {
        return <MessageScrollerRoot
            {...props}
        />;
    }

    return <MessageScrollerProvider
        customTheme={props.customTheme}
    >
        <MessageScrollerRoot
            {...props}
        />
    </MessageScrollerProvider>;
};

export default Object.assign(MessageScroller, {
    useContext: useMessageScrollerState,
    Provider: MessageScrollerProvider,
    Viewport: MessageScrollerViewport,
    Content: MessageScrollerContent,
    Button: MessageScrollerButton,
    Footer: MessageScrollerFooter,
    Item: MessageScrollerItem
});
